/***
 *
 *   DASHBOARD
 *   Template dashboard example demonstrating various components inside a view.
 *
 **********/

import axios from "axios";
import { FieldInput } from "components/fieldInput";
import {
  Animate,
  AuthContext,
  Icon,
  ViewContext,
  useLocation,
  useNavigate,
} from "components/lib";
import { stateOptionsFullNames } from "data";
import { Form, Formik } from "formik";
import { Fragment, useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import logo from "./images/black_logo.png";
import LoaderGif from "./images/loader.gif";
import styles from "./styles.module.scss";

export function OnboardingPage(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { user } = useContext(AuthContext);
  const [needUpdate, setNeedUpdate] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (location.state) {
      setNeedUpdate(location.state.documentsList);
    } else if (location.search.includes("?update=")) {
      navigate("/app/onboarding", {
        state: {
          documentsList: [location.search.split("=")[1]],
        },
      });
    }
  }, [location]);

  const validationSchema = Yup.object().shape({
    licenses: Yup.array()
      .min(1, "License(s) file(s) are required")
      .required("License(s) file(s) are required"),
    eo: Yup.array()
      .min(1, "E&O file(s) are required")
      .required("E&O file(s) are required"),
    w9: Yup.mixed().required("W-9 file are required"),
    contract: Yup.mixed().required("Agency Contract file are required"),
    /*     agreement: Yup.mixed().required("Compensation Agreement file are required"), */
  });

  return (
    <Animate type="pop">
      <div className={styles.container}>
        <div className={styles.header}>
          <img src={logo} alt="Logo" />
        </div>
        <div className={styles.wrapper}>
          {needUpdate.length > 0 ? (
            <>
              <h1>Update your documents</h1>
              <span>Please upload the new documents below</span>
            </>
          ) : (
            <>
              <h1>Finish your account creation</h1>
              <span>
                Please upload the documents below to set up your account
              </span>
            </>
          )}

          <Formik
            validationSchema={needUpdate ? null : validationSchema}
            initialValues={{
              licenses: [],
              eo: [],
              w9: "",
              contract: "",
              /*             agreement: "", */
            }}
            onSubmit={async (values, { setErrors }) => {
              var errors = {};
              const { licenses, eo, w9, contract } = values;
              setSending(true);

              //Verify licenses
              for (var i = 0; i < licenses.length; i++) {
                const dateElementId = `license_${i}_date`;
                const stateElementId = `license_${i}_state`;
                const date = document.getElementById(dateElementId);
                const state = document.getElementById(stateElementId);

                if (
                  !date.value ||
                  new Date(date.value).getTime() <= new Date().getTime()
                ) {
                  errors[`license_${i}_date`] = "Invalid date";
                }

                if (!state.value || state.value === "") {
                  errors[`license_${i}_state`] = "Invalid state";
                }
              }

              //Verify eo
              for (var a = 0; a < eo.length; a++) {
                const dateElementId = `eo_${a}_date`;

                const date = document.getElementById(dateElementId);

                if (
                  !date.value ||
                  new Date(date.value).getTime() <= new Date().getTime()
                ) {
                  errors[`eo_${a}_date`] = "Invalid date";
                }
              }

              if (Object.values(errors).length === 0) {
                const documentsArray = [];
                //Upload licenses
                for (var l = 0; l < licenses.length; l++) {
                  const index = l;
                  const license = licenses[index];

                  const stateElement = document.getElementById(
                    `license_${index}_state`
                  );
                  const dateElement = document.getElementById(
                    `license_${index}_date`
                  );

                  await axios
                    .post(`/api/document`, {
                      name: license.name,
                      type: "license",
                      state: stateElement.value,
                      expiration_date: new Date(dateElement.value),
                      account_id: user.account_id,
                      status: "pending_review",
                    })
                    .then(async (res) => {
                      const form_data = new FormData();
                      form_data.append("file", license);
                      documentsArray.push(license.name);
                      await axios.post(
                        `/api/document/${res.data.data.id}/file`,
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                    })
                    
                    .catch((err) => {
                      setSending(false);

                      context.notification.show(
                        err.response.data.message,
                        "error",
                        true,
                        "toast",
                        "x-circle"
                      );
                    });
                }

                //Upload E&O
                for (var e = 0; e < eo.length; e++) {
                  const index = e;
                  const eoDoc = eo[e];

                  const dateElement = document.getElementById(
                    `eo_${index}_date`
                  );

                  await axios
                    .post(`/api/document`, {
                      name: eoDoc.name,
                      type: "eo",
                      expiration_date: new Date(dateElement.value),
                      account_id: user.account_id,
                      status: "pending_review",
                    })
                    .then(async (res) => {
                      const form_data = new FormData();
                      form_data.append("file", eoDoc);
                      documentsArray.push(eoDoc.name);
                      await axios.post(
                        `/api/document/${res.data.data.id}/file`,
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                    })
                    .catch((err) => {
                      setSending(false);

                      context.notification.show(
                        err.response.data.message,
                        "error",
                        true,
                        "toast",
                        "x-circle"
                      );
                    });
                }

                //Upload W9
                if (w9.files) {
                  const w9File = w9.files[0];

                  await axios
                    .post(`/api/document`, {
                      name: w9File.name,
                      type: "w9",
                      account_id: user.account_id,
                      status: "pending_review",
                    })
                    .then(async (res) => {
                      const form_data = new FormData();
                      form_data.append("file", w9File);
                      documentsArray.push(w9File.name);
                      await axios.post(
                        `/api/document/${res.data.data.id}/file`,
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                    })
                    .catch((err) => {
                      setSending(false);

                      context.notification.show(
                        err.response.data.message,
                        "error",
                        true,
                        "toast",
                        "x-circle"
                      );
                    });
                }

                //Upload Agency Contract
                if (contract.files) {
                  const agencyContractFile = contract.files[0];

                  await axios
                    .post(`/api/document`, {
                      name: agencyContractFile.name,
                      type: "agency_contract",
                      account_id: user.account_id,
                      status: "pending_review",
                    })
                    .then(async (res) => {
                      const form_data = new FormData();
                      form_data.append("file", agencyContractFile);
                      documentsArray.push(agencyContractFile.name);
                      await axios.post(
                        `/api/document/${res.data.data.id}/file`,
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                    })
                    .catch((err) => {
                      setSending(false);

                      context.notification.show(
                        err.response.data.message,
                        "error",
                        true,
                        "toast",
                        "x-circle"
                      );
                    });
                }

                /*                   //Upload Compensation Agreement
                  const compensationAgreementFile = agreement.files[0];

                  await axios
                    .post(`/api/document`, {
                      name: compensationAgreementFile.name,
                      type: "compensation_agreement",
                      account_id: user.account_id,
                    })
                    .then(async (res) => {
                      const form_data = new FormData();
                      form_data.append("file", compensationAgreementFile);

                      await axios.post(
                        `/api/document/${res.data.data.id}/file`,
                        form_data,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                    })
                    .catch((err) => {
                      context.notification.show(
                        err.response.data.message,
                        "error",
                        true,
                        "toast",
                        "x-circle"
                      );
                    }); */
              await axios
                .post(`/api/user/agency-email`, {
                  agency_id: user.account_id,
                  documents: documentsArray,
                })
                .then(async (res) => {
                  console.log("res: ", res);
                })
              } else {
                setErrors(errors);
                setSending(false);

                return;
              }

              setSending(false);
              window.location = "/app/dashboard";
            }}
          >
            {({
              errors,
              touched,
              handleSubmit,
              handleChange,
              handleBlur,
              submitForm,
              setFieldValue,
              values,
            }) => (
              <Fragment>
                <Form
                  className={styles.form}
                  onSubmit={handleSubmit}
                  encType="multipart/form-data"
                >
                  {(needUpdate.length === 0 ||
                    (needUpdate && needUpdate.includes("licenses"))) && (
                    <div
                      className={styles.fieldRow}
                      style={{ flexDirection: "column" }}
                    >
                      <FieldInput
                        id="licenses"
                        type="upload_multiple"
                        label="Licenses"
                        handleChange={(files_callback) => {
                          handleChange("licenses");
                          setFieldValue(
                            "licenses",
                            Array.from(files_callback.files)
                          );
                        }}
                        handleBlur={handleBlur}
                        showErrors
                        errors={errors.licenses}
                        touched={touched.licenses}
                        required
                        strongTitle
                      />
                      {values.licenses &&
                        values.licenses &&
                        values.licenses.map((value, index) => {
                          return (
                            <div className={styles.file} key={index}>
                              <div className={styles.data}>
                                <FieldInput
                                  id={`license_${index}_state`}
                                  type="state_select"
                                  label="State"
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  showErrors
                                  errors={errors[`license_${index}_state`]}
                                  touched={true}
                                  options={[
                                    { label: "Select a option", value: "" },
                                    ...stateOptionsFullNames,
                                  ]}
                                />
                                <FieldInput
                                  id={`license_${index}_date`}
                                  type="date"
                                  label="Expiration Date"
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  showErrors
                                  errors={errors[`license_${index}_date`]}
                                  touched={touched[`license_${index}_date`]}
                                />
                              </div>
                              <div className={styles.file_details}>
                                <div className={styles.left}>
                                  <Icon image="file" size={22} />
                                  <span>
                                    {value.name.length > 30
                                      ? value.name.slice(0, 30 - 3) + "..."
                                      : value.name}
                                  </span>
                                </div>
                                <div className={styles.right}>
                                  <div
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      const dataTransfer = new DataTransfer();

                                      values.licenses.splice(index, 1);
                                      values.licenses.forEach((file) => {
                                        dataTransfer.items.add(file);
                                      });

                                      handleChange("licenses");
                                      setFieldValue(
                                        "licenses",
                                        values.licenses
                                      );
                                      document.getElementById(
                                        "licenses"
                                      ).files = dataTransfer.files;
                                    }}
                                  >
                                    <Icon image="x" size={22} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                  {(needUpdate.length === 0 ||
                    (needUpdate && needUpdate.includes("eo"))) && (
                    <div
                      className={styles.fieldRow}
                      style={{ flexDirection: "column" }}
                    >
                      <FieldInput
                        id="eo"
                        type="upload_multiple"
                        label="E&O"
                        handleChange={(files_callback) => {
                          handleChange("eo");
                          setFieldValue("eo", Array.from(files_callback.files));
                        }}
                        handleBlur={handleBlur}
                        showErrors
                        errors={errors.eo}
                        touched={touched.eo}
                        required
                        strongTitle
                      />
                      {values.eo &&
                        values.eo &&
                        values.eo.map((value, index) => {
                          return (
                            <div className={styles.file} key={index}>
                              <div className={styles.data}>
                                <FieldInput
                                  id={`eo_${index}_date`}
                                  type="date"
                                  label="Expiration Date"
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  showErrors
                                  errors={errors[`eo_${index}_date`]}
                                  touched={touched[`eo_${index}_date`]}
                                />
                              </div>
                              <div className={styles.file_details}>
                                <div className={styles.left}>
                                  <Icon image="file" size={22} />
                                  <span>{value.name}</span>
                                </div>
                                <div className={styles.right}>
                                  <div
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      const dataTransfer = new DataTransfer();

                                      values.eo.splice(index, 1);
                                      values.eo.forEach((file) => {
                                        dataTransfer.items.add(file);
                                      });

                                      handleChange("eo");
                                      setFieldValue("eo", values.eo);
                                      document.getElementById("eo").files =
                                        dataTransfer.files;
                                    }}
                                  >
                                    <Icon image="x" size={22} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                  {(needUpdate.length === 0 ||
                    (needUpdate && needUpdate.includes("w9"))) && (
                    <div className={styles.fieldRow}>
                      <FieldInput
                        id="w9"
                        type="upload"
                        label="W-9"
                        handleChange={(file) => {
                          handleChange("w9");
                          setFieldValue("w9", file);
                        }}
                        handleBlur={handleBlur}
                        showErrors
                        errors={errors.w9}
                        touched={touched.w9}
                        required
                        strongTitle
                      />
                    </div>
                  )}
                  {(needUpdate.length === 0 ||
                    (needUpdate && needUpdate.includes("agency_contract"))) && (
                    <div className={styles.fieldRow}>
                      <FieldInput
                        id="contract"
                        type="upload"
                        label="Agency Contract"
                        handleChange={(file) => {
                          handleChange("contract");
                          setFieldValue("contract", file);
                        }}
                        handleBlur={handleBlur}
                        showErrors
                        errors={errors.contract}
                        touched={touched.contract}
                        required
                        strongTitle
                      />
                    </div>
                  )}
                  {/*             <div className={styles.fieldRow}>
                      <FieldInput
                        id="agreement"
                        type="upload"
                        label="Compensation Agreement"
                        handleChange={(file) => {
                          handleChange("agreement");
                          setFieldValue("agreement", file);
                        }}
                        handleBlur={handleBlur}
                        showErrors
                        errors={errors.agreement}
                        touched={touched.agreement}
                        required
                        strongTitle
                      />
                    </div> */}
                </Form>
                <div
                  className={styles.footer}
                  style={{ justifyContent: "flex-end" }}
                >
                  <div className={styles.buttons}>
                    <button
                      className={styles.save}
                      onClick={!sending ? submitForm : null}
                      style={
                        sending ? { cursor: "default" } : { cursor: "pointer" }
                      }
                    >
                      {sending ? (
                        <img
                          src={LoaderGif}
                          alt="Loader"
                          className={styles.loader}
                        />
                      ) : (
                        "Send"
                      )}
                    </button>
                  </div>
                </div>
              </Fragment>
            )}
          </Formik>
        </div>
        <div className={styles.footer}>Coverbadger ©2025</div>
      </div>
    </Animate>
  );
}
